import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';

export const fetchDropdownData = createAsyncThunk('timesheet/fetchDropdownData', async () => {
  const projectsResponse = await api.get(ENDPOINTS.PROJECT_DROPDOWNS_BY_USER);
  
  if (!projectsResponse?.data?.data || !Array.isArray(projectsResponse.data.data)) {
    throw new Error('Invalid projects response format');
  }
  
  return {
    projects: projectsResponse.data.data,
    scrumSprints: [],
    type: [],
    priority: [],
    status: []
  };
});

export const fetchTimesheetAssignees = createAsyncThunk(
  'timesheet/fetchTimesheetAssignees',
  async (projectIds = [], { rejectWithValue }) => {
    try {
      const projectIdsParam = Array.isArray(projectIds) && projectIds.length > 0 ? `&projectIds=${projectIds.join(',')}` : '';
      const response = await api.get(`${ENDPOINTS.TIMESHEET_DROPDOWNS('users')}${projectIdsParam}`);
      if (!response?.data?.data || !Array.isArray(response.data.data)) {
        throw new Error('Invalid users response format');
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch assignees');
    }
  }
);

export const fetchTimesheetScrumSprintDropdowns = createAsyncThunk(
  'timesheet/fetchTimesheetScrumSprintDropdowns',
  async (projectIds = []) => {
    if (!Array.isArray(projectIds) || projectIds.length === 0) return [];

    // Timesheet supports multiple Scrum projects, so collect each project's
    // sprint_details list and flatten it into one multi-select option set.
    const responses = await Promise.all(projectIds.map(async projectId => ({
      projectId,
      response: await api.get(`${ENDPOINTS.TICKETS_DROPDOWNS}?project_id=${projectId}&type=sprint&include_completed=true`)
    })));

    // The focused sprint API returns compact sprint data without its project ID.
    // Preserve request ownership so the Timesheet selector can group by project.
    return responses.flatMap(({ projectId, response }) => (
      (response.data?.data || []).map(sprint => ({ ...sprint, project_id: projectId }))
    ));
  }
);

export const fetchTimesheetData = createAsyncThunk(
  'timesheet/fetchTimesheetData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.TIMESHEET, { params });
      return response.data.data;
    } catch (error) {
      let message = 'Failed to fetch timesheet data';
      if (error.response && error.response.data && error.response.data.message) {
      message = error.response.data.message;
      } else if (error.message) {
      message = error.message;
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchUserTimesheetDetails = createAsyncThunk(
  'timesheet/fetchUserTimesheetDetails',
  async ({ userId, fromDate, toDate, projectIds, scrumSprintIds }, { rejectWithValue }) => {
    try {
      const params = { fromDate, toDate };
      if (projectIds) {
        params.projectIds = projectIds;
      }
      if (scrumSprintIds) {
        params.scrum_sprint_id = scrumSprintIds;
      }
      const response = await api.get(ENDPOINTS.TIMESHEET_USER_DETAILS(userId), { params });
      return { userId, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user details');
    }
  }
);

export const exportTimesheetData = createAsyncThunk(
  'timesheet/exportTimesheetData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get(ENDPOINTS.TIMESHEET_EXPORT, {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to export data');
    }
  }
);

const initialState = {
  dropdownData: {
    projects: [],
    assignees: [],
    scrumSprints: [],
    type: [],
    priority: [],
    status: []
  },
  timesheetData: {
    users: [],
    total_working_hours: '0h 0m',
    daily_summary: []
  },
  userDetails: {},
  loading: false,
  timesheetLoading: false,
  userDetailsLoading: {},
  error: null
};

const timesheetSlice = createSlice({
  name: 'timesheet',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUserDetails: (state) => {
      state.userDetails = {};
      state.userDetailsLoading = {};
    },
    clearTimesheetData: (state) => {
      state.timesheetData = {
        users: [],
        total_working_hours: '0h 0m',
        daily_summary: []
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownData = {
          ...state.dropdownData,
          ...action.payload
        };
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTimesheetAssignees.fulfilled, (state, action) => {
        state.dropdownData.assignees = action.payload || [];
      })
      .addCase(fetchTimesheetScrumSprintDropdowns.fulfilled, (state, action) => {
        state.dropdownData.scrumSprints = action.payload || [];
      })
      .addCase(fetchTimesheetData.pending, (state) => {
        state.timesheetLoading = true;
        state.error = null;
      })
      .addCase(fetchTimesheetData.fulfilled, (state, action) => {
        state.timesheetLoading = false;
        state.timesheetData = action.payload;
      })
      .addCase(fetchTimesheetData.rejected, (state, action) => {
        state.timesheetLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserTimesheetDetails.pending, (state, action) => {
        const userId = action.meta.arg.userId;
        state.userDetailsLoading[userId] = true;
      })
      .addCase(fetchUserTimesheetDetails.fulfilled, (state, action) => {
        const { userId, data } = action.payload;
        state.userDetailsLoading[userId] = false;
        state.userDetails[userId] = data;
      })
      .addCase(fetchUserTimesheetDetails.rejected, (state, action) => {
        const userId = action.meta.arg.userId;
        state.userDetailsLoading[userId] = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearUserDetails, clearTimesheetData } = timesheetSlice.actions;
export default timesheetSlice.reducer;
